import { Button, Pagination } from 'antd'
import { FC, memo, useState } from 'react'
import { useTranslation } from '@/services/i18n'
import styles from './index.module.scss'
import dayjs from 'dayjs'
import { Web3TransactionStatusEnum } from '@/constants/enum'
import { ReactComponent as ArrowIcon } from '@/assets/icons/arrowDown.svg'
import classNames from 'classnames'
import ExpandedRow from './ExpandedRow'
import ApprovalFlow from '@/components/ApprovalFlow'
import Table from '@/components/Table'
import { useGetWeb3SignRecords } from '@/hooks/wallet'
import { DEFAULT_PAGE_SIZE } from '@/constants'
import ShadowView from '@/components/ShadowView'

interface HistoryProps {
  walletKey: string
}

const txStatusColorClass = (value: Web3TransactionStatusEnum) => {
  const colorMap = {
    [Web3TransactionStatusEnum.CANCELLED]: 'normal',
    [Web3TransactionStatusEnum.SIGNING]: 'waiting',
    [Web3TransactionStatusEnum.SUBMITTED]: 'waiting',
    [Web3TransactionStatusEnum.FAILED]: 'danger',
    [Web3TransactionStatusEnum.REJECTED]: 'danger',
    [Web3TransactionStatusEnum.SIGN_COMPLETED]: 'success',
  }
  return colorMap[value]
}

const History: FC<HistoryProps> = ({ walletKey }) => {
  const { t } = useTranslation()
  const { loading, currentPage, data, setCurrentPage } = useGetWeb3SignRecords(walletKey)
  const [expandedRowKeys, setExpandedRowKeys] = useState<Record<string, boolean>>({})
  const columns = [
    {
      title: t('txRecord.time'),
      dataIndex: 'gmtCreate',
      className: styles.firstColumn,
      render: (value: string, item: any) => {
        return dayjs(value).format('YYYY-MM-DD HH:mm')
      },
    },
    {
      title: t('txRecord.wallet'),
      dataIndex: 'sourceAccountName',
    },
    {
      title: t('txRecord.txType'),
      dataIndex: 'title',
    },
    {
      title: t('txRecord.txDetail'),
      dataIndex: 'desc',
      width: '20%',
    },
    {
      title: t('txRecord.sender'),
      dataIndex: 'createUserName',
    },
    {
      title: t('txRecord.status'),
      dataIndex: 'transactionStatusDesc',
      width: '15%',
      render: (value: string, item: any) => {
        const colorClass = txStatusColorClass(item.transactionStatus)
        const cls = classNames([styles.statusDot, styles[colorClass]])
        return (
          <div className="flex items-center">
            <span className={cls} />
            {value}
          </div>
        )
      },
    },
    {
      title: t('txRecord.approvalFlow'),
      render: (_: any, item: any) => {
        return <ApprovalFlow flow={item?.auditRecordDto} />
      },
    },
    {
      render: (_: any, item: any) => {
        const isExpaned = expandedRowKeys[item.txKey]
        return (
          <Button
            type="link"
            onClick={() => {
              if (isExpaned) {
                delete expandedRowKeys[item.txKey]
                setExpandedRowKeys({
                  ...expandedRowKeys,
                })
              } else {
                setExpandedRowKeys({
                  ...expandedRowKeys,
                  [item.txKey]: true,
                })
              }
            }}
          >
            {t('common.detail')}
            <ArrowIcon className={isExpaned ? styles.arrowIconDown : styles.arrowIcon} />
          </Button>
        )
      },
    },
  ]

  const showPagination = Math.ceil((data?.totalElements ?? 0) / DEFAULT_PAGE_SIZE) > 1

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>{t('wallet.web3Detail.title')}</h3>
      <Table
        expandable={{
          expandedRowRender: (record: any) => <ExpandedRow data={record} />,
          expandedRowKeys: Object.keys(expandedRowKeys),
          showExpandColumn: false,
        }}
        pagination={false}
        columns={columns}
        dataSource={data?.content}
        rowKey={item => item.txKey}
        loading={loading}
        bottomY={74}
      />
      {showPagination && (
        <>
          <ShadowView style={{ bottom: 74 }} />
          <div className="pagination">
            <Pagination
              current={currentPage}
              onChange={setCurrentPage}
              total={data?.totalElements}
              showQuickJumper
              showSizeChanger={false}
              showTotal={total => t('common.total', { total })}
              pageSize={DEFAULT_PAGE_SIZE}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default memo(History)
